import { differenceInHours, format, startOfWeek } from "date-fns";
import { useMemo } from "react";

import { EmailType } from "@interfaces/campaign.type";

export type RangeType = "24h" | "allTime";

const twentyFourHoursDataSet = Array.from({ length: 24 })
  .fill(0)
  .map((_, index) => index);

export type DataSetType = {
  linkClicked: number[];
  opened: number[];
  replyReceived: number[];
};

interface ISeriesData {
  labels: string[];
  series: DataSetType;
}

const zeroFilledArray24 = Array.from({ length: 24 }).fill(0) as number[];

const updateSeries = (series: DataSetType, property: keyof DataSetType, hour: number) => {
  return {
    ...series,
    [property]: series[property].map((value, index) => (index + 1 === hour ? value + 1 : value)),
  };
};

const getDataForTwentyFourHours = (emails: EmailType[], twentyFourHoursDataSet: number[]): ISeriesData => {
  // Initialize series object with arrays filled with zeros for each hour of the day
  let series: DataSetType = {
    linkClicked: zeroFilledArray24,
    opened: zeroFilledArray24,
    replyReceived: zeroFilledArray24,
  };

  for (const email of emails) {
    // Get the createdAt timestamp of the email (assuming it's in ISO string format)
    const emailCreatedAt = new Date(email.created);

    // Iterate through each event of the email
    for (const event of email.emailEvents) {
      // Get the timestamp of the event
      const eventCreatedAt = new Date(event.createdAt);

      // Calculate the difference in hours between email createdAt and event createdAt
      const hoursDifference = differenceInHours(emailCreatedAt, eventCreatedAt);

      // Check if the event is within the first 24 hours of email createdAt
      if (hoursDifference < 24) {
        // Increment the corresponding hour in the series object based on eventName
        const hour = eventCreatedAt.getHours();
        switch (event.eventName) {
          case "clicked": {
            series = updateSeries(series, "linkClicked", hour);
            break;
          }
          case "opened": {
            series = updateSeries(series, "opened", hour);
            break;
          }
          case "replied": {
            series = updateSeries(series, "replyReceived", hour);
            break;
          }
          // No default
        }
      }
    }
  }

  // Generate labels for each hour in a day format (e.g., "00:00h", "01:00h", ..., "23:00h")
  const labels = twentyFourHoursDataSet.map((hour) => {
    const formattedHour = hour < 10 ? `0${hour}` : hour;
    return `${formattedHour}:00h`;
  });
  return { labels, series };
};

const groupDataByWeeks = (emails: EmailType[]): ISeriesData => {
  // Initialize weeks object to store counts per week
  const weeks: {
    [weekLabel: string]: {
      linkClicked: number;
      opened: number;
      replyReceived: number;
    };
  } = {};

  // Process each email and its events
  for (const email of emails) {
    // Iterate through email events
    for (const event of email.emailEvents) {
      const eventDate = new Date(event.createdAt);
      const weekStart = startOfWeek(eventDate);
      const weekLabel = format(weekStart, "yyyy-MM-dd");

      // Ensure the week object exists for the current week label
      if (!weeks[weekLabel]) {
        weeks[weekLabel] = {
          linkClicked: 0,
          opened: 0,
          replyReceived: 0,
        };
      }

      // Accumulate counts for each type within the current week
      switch (event.eventName) {
        case "clicked": {
          weeks[weekLabel].linkClicked++;
          break;
        }
        case "opened": {
          weeks[weekLabel].opened++;
          break;
        }
        case "replied": {
          weeks[weekLabel].replyReceived++;
          break;
        }
        // No default
      }
    }
  }

  // Prepare labels and series arrays for output
  const labels = Object.keys(weeks);

  const series: DataSetType = {
    linkClicked: [],
    opened: [],
    replyReceived: [],
  };

  // Populate series arrays with data from weeks object
  for (const weekLabel of labels) {
    if (weeks && weeks[weekLabel]) {
      series.linkClicked.push(weeks[weekLabel].linkClicked || 0);
      series.opened.push(weeks[weekLabel].opened || 0);
      series.replyReceived.push(weeks[weekLabel].replyReceived || 0);
    }
  }

  return { labels, series };
};

const getSeries = (data: EmailType[], activeRange: RangeType) => {
  switch (activeRange) {
    case "24h": {
      return getDataForTwentyFourHours(data, twentyFourHoursDataSet);
    }
    case "allTime": {
      return groupDataByWeeks(data);
    }
  }
};

const getXAxis = (seriesData: ISeriesData, activeRange: RangeType) => {
  switch (activeRange) {
    case "24h": {
      return {
        data: twentyFourHoursDataSet,
        valueFormatter: (value: number) => {
          const formattedValue = value < 10 ? `0${value}` : value;
          return `${formattedValue}:00h`;
        },
      };
    }
    case "allTime": {
      return {
        data: seriesData.labels.map((_, index) => index),
        valueFormatter: (value: number) => seriesData.labels[value] || "",
      };
    }
    default: {
      return {
        data: [],
        valueFormatter: () => "",
      };
    }
  }
};

const useChartRange = (data: EmailType[], activeRange: RangeType) => {
  return useMemo(() => {
    const seriesData = getSeries(data, activeRange);

    const xAxis = getXAxis(seriesData, activeRange);

    return {
      series: {
        // we use this to properly sort the data by going - opened, linkClicked, replyReceived
        linkClicked: seriesData.series.linkClicked,
        opened: seriesData.series.opened,
        replyReceived: seriesData.series.replyReceived,
      },
      xAxis,
    };
  }, [data, activeRange]);
};

export default useChartRange;
